/*
 * File: addressesApi.ts
 * Project: rosita
 * -----
 * Last Modified: 24 Feb 2022 11:31:18 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { AddressPayload, AddressValidationPayload } from 'models/Address';
import { ChartDataModel } from 'models/Reports';
import { ReduxState } from 'redux/store';
import { countryChartGenerator, googleVerifiedChartGenerator, stateChartGenerator, typeChartGenerator } from 'utils/chartDataGenerator';
import { Address, GetAddressesParams, GetAddressesResponse } from './models/addresses';

/* ******************** Base Query******************** */
const baseUrl = process.env.REACT_APP_CHUCKIE_SUE_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_CHUCKIE_SUE;

export const chuckieSueAddressesApi = createApi({
  reducerPath: 'chuckieSueAddressesApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as ReduxState).app.accessToken;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);
        headers.set('Content-Type', 'application/json');
      }

      return headers;
    }
  }),
  tagTypes: ['Address', 'Addresses', 'AddressValidation', 'Charts'],
  endpoints: (builder) => ({
    getAddresses: builder.query<GetAddressesResponse, { bussinessId: string | null; params: GetAddressesParams }>({
      query: ({ bussinessId, params }) => ({
        url: `/businesses/${bussinessId}/addresses`,
        params
      }),
      // Serialize args without offset - treats all pages as same cache entry
      serializeQueryArgs: ({ queryArgs }: { queryArgs: { bussinessId: string | null; params: GetAddressesParams } }) => {
        const { params, bussinessId } = queryArgs;
        if (!params) return { bussinessId };
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { offset, ...filterParams } = params;
        return { bussinessId, params: filterParams };
      },
      // Merge new page data with existing cached data
      merge: (currentCache: GetAddressesResponse, newItems: GetAddressesResponse, { arg }: { arg: { bussinessId: string | null; params: GetAddressesParams } }) => {
        // If offset is 0, replace cache (new search or filter change)
        if (!arg.params?.offset || arg.params.offset === 0) {
          return newItems;
        }
        // Append new items to existing cache
        return {
          ...newItems,
          data: [...currentCache.data, ...newItems.data]
        };
      },
      // Force refetch when offset changes (for pagination)
      forceRefetch: ({ currentArg, previousArg }: { currentArg?: { bussinessId: string | null; params: GetAddressesParams }; previousArg?: { bussinessId: string | null; params: GetAddressesParams } }) => {
        return currentArg?.params?.offset !== previousArg?.params?.offset;
      },
      providesTags: ['Addresses']
    }),
    getAddressesChartData: builder.query<ChartDataModel, { bussinessId: string | null; params: GetAddressesParams }>({
      query: ({ bussinessId, params }) => ({
        url: `/businesses/${bussinessId}/addresses`,
        params
      }),
      transformResponse: (data: any) => {
        const typeChart = typeChartGenerator(data.data);
        const stateChart = stateChartGenerator(data.data);
        const countryChart = countryChartGenerator(data.data);
        const verifiedChart = googleVerifiedChartGenerator(data.data);
        const fullData = data.data;

        return { typeChart, stateChart, countryChart, verifiedChart, fullData };
      },
      providesTags: ['Charts']
    }),
    getAddress: builder.query<Address, { bussinessId: string | null; addressId: string | null }>({
      query: ({ bussinessId, addressId }) => ({
        url: `/businesses/${bussinessId}/addresses/${addressId}`
      }),
      providesTags: ['Address']
    }),
    createAddress: builder.mutation<Address, { bussinessId: string | null; payload: AddressPayload; skipValidation: boolean }>({
      query: ({ bussinessId, payload, skipValidation }) => ({
        url: `/businesses/${bussinessId}/addresses/?skipAddressValidation=${skipValidation}`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['Addresses', 'Address', 'Charts']
    }),
    recoverAddress: builder.mutation<Address, { bussinessId: string | null; addressId: string | null }>({
      query: ({ bussinessId, addressId }) => ({
        url: `/businesses/${bussinessId}/addresses/${addressId}/recover`
      }),
      invalidatesTags: ['Addresses', 'Address', 'Charts']
    }),
    deleteAddress: builder.mutation<Address, { bussinessId: string; addressId: string; params: { wouldYouLikeToPlayAGame: boolean } }>({
      query: ({ bussinessId, addressId, params }) => ({
        url: `/businesses/${bussinessId}/addresses/${addressId}`,
        method: 'DELETE',
        params
      }),
      invalidatesTags: ['Addresses', 'Address', 'Charts']
    }),
    updateAddress: builder.mutation<Address, { bussinessId: string; addressId: string; payload: AddressPayload; params: { skipAddressValidation: boolean } }>({
      query: ({ bussinessId, addressId, payload, params }) => ({
        url: `/businesses/${bussinessId}/addresses/${addressId}`,
        method: 'PATCH',
        body: payload,
        params
      }),
      invalidatesTags: ['Addresses', 'Address', 'Charts']
    }),
    validateAddress: builder.query<Address, { bussinessId: string | null; payload: AddressValidationPayload }>({
      query: ({ bussinessId, payload }) => ({
        url: `/businesses/${bussinessId}/addresses/validate`,
        method: 'POST',
        body: payload
      }),
      providesTags: ['AddressValidation']
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const {
  useGetAddressesQuery,
  useGetAddressesChartDataQuery,
  useGetAddressQuery,
  useLazyGetAddressesQuery,
  useGetVersionQuery,
  useGetApiNameQuery,
  useCreateAddressMutation,
  useDeleteAddressMutation,
  useRecoverAddressMutation,
  useUpdateAddressMutation,
  useValidateAddressQuery
} = chuckieSueAddressesApi;
